import React, { useState } from 'react';

import { SecondaryButton } from '@components/Button/variants';
import IconCurvedArrow from '@components/Images/Rebranding/IconCurvedArrow';
import NewsletterActionButton from '@components/pages/NewsletterIndexLanding/NewsletterCard/NewsletterActionButton';
import { Offers } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ContentType, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';

import {
  ARCHIVES,
  PERSONALIZED_BRIEFING,
  SHARE,
} from '../NewsletterIndexLanding/NewsletterCard/interfaces';
import ShareModal from './ShareModal';

interface NewsletterActionProps {
  canSubscribeToNewsletter: boolean;
  currentUserSubscribed: boolean;
  setCurrentSubscribeStatus: React.Dispatch<React.SetStateAction<boolean>>;
  initialCurrentUser: User;
  name: string;
  id: number;
  description: string;
  author: string;
  isCommingSoon: boolean;
  readLatestPath: string;
  isPro: boolean;
  afterSignUpUrl: string;
  offers: Offers;
}

const NewsletterAction = ({
  canSubscribeToNewsletter,
  currentUserSubscribed,
  setCurrentSubscribeStatus,
  description,
  initialCurrentUser,
  id,
  name,
  author,
  isCommingSoon,
  readLatestPath,
  isPro,
  afterSignUpUrl,
  offers,
}: NewsletterActionProps) => {
  const { trackEvent } = useEventTracking({
    contentType: ContentType.Newsletter,
    contentId: id,
  });
  const [showShareModal, setShowShareModal] = useState(false);

  const shareToggleModal = () => setShowShareModal((prev) => !prev);

  const handleClickTracking = (clickTarget: ClickTarget, label: string) => {
    trackEvent(click({ clickTarget: clickTarget, clickText: label }));
  };

  return (
    <>
      <div className="mt-24 flex flex-wrap gap-12">
        <NewsletterActionButton
          isPersonalizedBriefing={name.includes(PERSONALIZED_BRIEFING)}
          newsletterName={name}
          canSubscribeToNewsletter={canSubscribeToNewsletter}
          currentUserSubscribed={currentUserSubscribed}
          initialCurrentUser={initialCurrentUser}
          onClickCapture={handleClickTracking}
          isNewsletterLanding
          isPro={isPro}
          id={id}
          afterSignUpUrl={afterSignUpUrl}
          offers={offers}
          isCurrentlySubscriber={initialCurrentUser?.is_subscriber}
          isProSubscriber={initialCurrentUser?.is_pro_subscriber}
          updateStateHandlerOnSubscribe={() => setCurrentSubscribeStatus(true)}
          updateStateHandlerOnUnsubscribe={() => setCurrentSubscribeStatus(false)}
        />
        <SecondaryButton
          as="button"
          className="group flex !max-w-[90px] items-center gap-8 rounded-[100px] !border-grayscale-gray bg-white py-6 pl-6 pr-12 hover:!bg-primary-pink focus:bg-white enabled:hover:!bg-primary-pink"
          onClick={() => shareToggleModal()}
          onClickCapture={() => handleClickTracking(ClickTarget.ShareButton, SHARE)}
        >
          <IconCurvedArrow className="stroke-white text-grayscale-gray group-hover:stroke-primary-pink group-hover:text-grayscale-charcoal dark:group-hover:text-black" />
          <span className="font-suisse-intl text-button-sm text-grayscale-gray group-hover:text-grayscale-charcoal dark:group-hover:text-black">
            {SHARE}
          </span>
        </SecondaryButton>
        {!isCommingSoon && (
          <SecondaryButton
            as="link"
            className="group flex !max-w-[90px] items-center gap-8 rounded-[100px] !border-grayscale-gray bg-white py-6 pl-12 pr-12 hover:!bg-primary-pink focus:bg-white enabled:hover:!bg-primary-pink"
            href={readLatestPath}
            onClickCapture={() => handleClickTracking(ClickTarget.Archive, ARCHIVES)}
          >
            <span className="font-suisse-intl text-button-sm text-grayscale-gray group-hover:text-grayscale-charcoal dark:group-hover:text-black">
              {ARCHIVES}
            </span>
          </SecondaryButton>
        )}
      </div>
      {showShareModal && (
        <ShareModal
          id={id}
          name={name}
          onClose={shareToggleModal}
          author={author}
          description={description}
        />
      )}
    </>
  );
};

export default NewsletterAction;
